import { Plus } from 'lucide-react';
import * as React from 'react';
import { formatPlainDate } from '@cfl/shared';

import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Combobox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Skeleton,
  Textarea,
  toast,
} from '@/components/cargofl-ui';
import { EmptyState } from '@/components/empty-state';
import { PlanArt } from '@/components/illustrations';
import { PageContainer } from '@/components/shell/page-container';
import { PlainDatePicker } from '@/components/plain-date-picker';
import { StatTile } from '@/components/stat-tile';
import { PILL_CLASS } from '@/lib/badges';
import { useEmployees } from '@/lib/people-api';
import { useClosePip, useCreatePip, usePips, type Pip, type PipStatus } from '@/lib/talent-api';

function err(e: unknown): string | undefined {
  return e instanceof Error ? e.message : undefined;
}

const STATUS_LABEL: Record<PipStatus, string> = {
  ACTIVE: 'Active',
  COMPLETED_SUCCESS: 'Completed — improved',
  COMPLETED_FAILED: 'Completed — not met',
  CANCELLED: 'Cancelled',
};

const STATUS_VARIANT: Record<PipStatus, 'soft-warning' | 'soft-success' | 'soft-destructive' | 'soft-muted'> = {
  ACTIVE: 'soft-warning',
  COMPLETED_SUCCESS: 'soft-success',
  COMPLETED_FAILED: 'soft-destructive',
  CANCELLED: 'soft-muted',
};

function CreateDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const [search, setSearch] = React.useState('');
  const { data: people, isFetching, error: searchError } = useEmployees({
    search: search || undefined,
    pageSize: 20,
  });
  const create = useCreatePip();

  const [employee, setEmployee] = React.useState<{ id: string; name: string } | null>(null);
  const employeeId = employee?.id ?? '';
  const [reason, setReason] = React.useState('');
  const [objectives, setObjectives] = React.useState('');
  const [startsOn, setStartsOn] = React.useState('');
  const [endsOn, setEndsOn] = React.useState('');

  // The person already picked stays on offer even after the search moves past them.
  const options = React.useMemo(() => {
    const found = (people?.items ?? []).map((p) => ({
      value: p.id,
      label: p.name,
      description: p.email,
      hint: p.designationTitle ?? undefined,
    }));
    return employee && !found.some((o) => o.value === employee.id)
      ? [{ value: employee.id, label: employee.name }, ...found]
      : found;
  }, [people, employee]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void create
              .mutateAsync({ employeeId, reason, objectives: objectives || null, startsOn, endsOn })
              .then(() => {
                toast.success('Improvement plan started', {
                  description: 'The employee has been notified.',
                });
                onOpenChange(false);
                setEmployee(null);
                setReason('');
                setObjectives('');
              })
              .catch((x: unknown) => toast.error('Could not start plan', { description: err(x) }));
          }}
        >
          <DialogHeader>
            <DialogTitle>Start an improvement plan</DialogTitle>
            <DialogDescription>
              The employee is notified immediately and their status changes to On PIP. Be specific
              about what has to change and by when — a plan without measurable objectives is not
              defensible.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <Label htmlFor="pipEmployee">Employee</Label>
              <Combobox
                id="pipEmployee"
                value={employeeId || null}
                // Select-only. An improvement plan is written against a person who
                // already works here; there is nothing here to create.
                onChange={(id) => {
                  const picked = (people?.items ?? []).find((p) => p.id === id);
                  if (picked) setEmployee({ id: picked.id, name: picked.name });
                }}
                options={options}
                onSearchChange={setSearch}
                loading={isFetching}
                error={searchError ? 'Could not search people. Try again.' : null}
                searchPlaceholder="Search by name or email"
                emptyText="Nobody matches"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="reason">Reason</Label>
              <Textarea
                id="reason"
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="obj">Objectives</Label>
              <Textarea
                id="obj"
                rows={3}
                value={objectives}
                onChange={(e) => setObjectives(e.target.value)}
                placeholder="What specifically must improve, and how it will be measured"
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="from">Starts</Label>
                <PlainDatePicker id="from" value={startsOn} onChange={setStartsOn} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="to">Ends</Label>
                <PlainDatePicker id="to" value={endsOn} onChange={setEndsOn} />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button hint={"Start the improvement plan"} type="submit" disabled={!employeeId || !reason.trim() || create.isPending}>
              Start plan
            </Button>
            {(!employeeId || !reason.trim()) && <span className="self-center text-xs text-muted-foreground">{!employeeId ? 'Choose the employee first' : 'Say why the plan is needed'}</span>}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PipCard({ pip }: { pip: Pip }) {
  const close = useClosePip();
  const [outcome, setOutcome] = React.useState('');

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-base">{pip.employeeName}</CardTitle>
          <CardDescription>
            {pip.departmentName ?? 'No department'} · {formatPlainDate(pip.startsOn)} –{' '}
            {formatPlainDate(pip.endsOn)}
            {pip.createdByName ? ` · started by ${pip.createdByName}` : ''}
          </CardDescription>
        </div>
        <Badge variant={STATUS_VARIANT[pip.status]} className={PILL_CLASS}>
          {STATUS_LABEL[pip.status]}
        </Badge>
      </CardHeader>

      <CardContent className="space-y-3">
        <div>
          <Label className="text-xs">Reason</Label>
          <p className="text-sm text-muted-foreground">{pip.reason}</p>
        </div>
        {pip.objectives && (
          <div>
            <Label className="text-xs">Objectives</Label>
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">{pip.objectives}</p>
          </div>
        )}
        {pip.outcome && (
          <div>
            <Label className="text-xs">Outcome</Label>
            <p className="text-sm text-muted-foreground">{pip.outcome}</p>
          </div>
        )}

        {pip.status === 'ACTIVE' && (
          <div className="space-y-2 rounded-md border p-3">
            <Label htmlFor={`out-${pip.id}`} className="text-xs">
              Close this plan
            </Label>
            <Textarea
              id={`out-${pip.id}`}
              rows={2}
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
              placeholder="What happened"
            />
            <div className="flex flex-wrap gap-2">
              {(
                [
                  ['COMPLETED_SUCCESS', 'Improved', 'default'],
                  ['COMPLETED_FAILED', 'Not met', 'destructive'],
                  ['CANCELLED', 'Cancel', 'outline'],
                ] as const
              ).map(([status, label, variant]) => (
                <Button hint={"Close this improvement plan"}
                  key={status}
                  size="sm"
                  variant={variant}
                  disabled={close.isPending}
                  onClick={() => {
                    void close
                      .mutateAsync({ id: pip.id, status, outcome: outcome || null })
                      .then(() => toast.success(`Plan closed: ${label}`))
                      .catch((e: unknown) =>
                        toast.error('Could not close', { description: err(e) }),
                      );
                  }}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function PipPage() {
  const [open, setOpen] = React.useState(false);
  const { data: pips, isPending } = usePips();

  const active = (pips ?? []).filter((p) => p.status === 'ACTIVE');
  const closed = (pips ?? []).filter((p) => p.status !== 'ACTIVE');
  const succeeded = closed.filter((p) => p.status === 'COMPLETED_SUCCESS').length;

  return (
    <PageContainer>
      <div className="grid gap-4 sm:grid-cols-3">
        <StatTile
          label="Active plans"
          value={active.length}
          tone={active.length > 0 ? 'warning' : 'good'}
          caption={active.length === 0 ? 'nobody is on a plan' : 'in progress right now'}
        />
        <StatTile
          label="Closed"
          value={closed.length}
          caption={
            closed.length === 0
              ? 'none closed yet'
              : `${succeeded} of ${closed.length} ended in improvement`
          }
        />
        <StatTile
          label="Success rate"
          value={closed.length === 0 ? '—' : `${Math.round((succeeded / closed.length) * 100)}%`}
          caption={
            closed.length === 0
              ? 'needs at least one closed plan'
              : 'of closed plans ended in improvement'
          }
        />
      </div>

      <div className="flex justify-end">
        <Button hint={"Start a performance improvement plan"} className="gap-2" onClick={() => setOpen(true)}>
          <Plus className="size-4" strokeWidth={2.4} />
          Start a plan
        </Button>
      </div>

      {isPending && <Skeleton className="h-40 w-full" />}

      {!isPending && (pips ?? []).length === 0 && (
        <Card>
          <CardContent>
            <EmptyState
              art={PlanArt}
              title="No improvement plans"
              body="A plan is a formal, time-boxed process. Use 1-on-1s and manager feedback first."
            />
          </CardContent>
        </Card>
      )}

      {active.length > 0 && (
        <Alert>
          <AlertDescription className="text-sm">
            An employee on a plan still has goals and assessments — a plan runs alongside the cycle,
            it does not replace it.
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        {[...active, ...closed].map((p) => (
          <PipCard key={p.id} pip={p} />
        ))}
      </div>

      <CreateDialog open={open} onOpenChange={setOpen} />
    </PageContainer>
  );
}
